import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import StackedItems from '@wtag/rcl-stacked-items';
import Avatar from '@wtag/rcl-avatar';
import { TYPE_FLIGHT } from 'sharedWebpack/helpers/itemTypes';
import JourneySummaryIndicator from 'sharedWebpack/JourneySummaryIndicator';
import classNames from 'classnames';
import ExchangeHeaderItem from './ExchangeHeaderItem';
import AirportShape from '../../../shapes/AirportShape';
import HeaderTop from './HeaderTop';
import getTripDetails from '../helpers/getTripDetails';
import getExchangeItems from '../helpers/getExchangeItems';
import getStackedTravelersLabel from '../helpers/getStackedTravelersLabel';
import getTripType from '../../../../lib/helpers/getTripType';
import { fetchJourneyElement } from '../../../../actions/common';
import './styles.scss';

const ExchangeHeader = ({
  travelers,
  airports,
  trips,
  isAirportLoading,
  fetchJourneyElementApi,
  exchange,
}) => {
  const {
    orderId,
    orderItemId,
    returnToPaths: { itemPath, overviewPath },
    supplierInfo: { identifier },
    journeyElementId,
  } = exchange;

  const tripType = getTripType(
    trips.map(({ origin, destination }) => ({ originCode: origin, destinationCode: destination })),
  );

  const { firstTrip, lastTrip, originInfo, destinationInfo } = getTripDetails(
    trips,
    airports,
    tripType,
  );

  const visibleTravelers = travelers.slice(0, 3);
  const extraTravelersCount = travelers.length - visibleTravelers.length;
  const stackedTravelerslLabel = getStackedTravelersLabel(extraTravelersCount);

  const [isTravelerPopoverVisible, setIsTravelerPopoverVisible] = useState(false);

  const toggleTravelerPopover = () => {
    setIsTravelerPopoverVisible(prev => !prev);
  };

  const closeTravelerPopover = () => {
    setIsTravelerPopoverVisible(false);
  };

  const items = getExchangeItems(orderId, orderItemId, identifier, overviewPath, itemPath);

  useEffect(() => {
    if (journeyElementId) {
      fetchJourneyElementApi(TYPE_FLIGHT, journeyElementId);
    }
  }, [journeyElementId]);

  return (
    <div className="flight-exchange-header d-flex-column row-gap-16">
      <HeaderTop
        startDate={firstTrip.date}
        endDate={lastTrip.date}
        itemPath={itemPath}
        originInfo={originInfo}
        destinationInfo={destinationInfo}
      />
      <div className="flight-exchange-header__flight-info d-flex align-center flex-wrap flex-gap-12">
        <JourneySummaryIndicator
          className="flight-exchange-header__indicator"
          originCountryInfo={originInfo}
          destinationCountryInfo={destinationInfo}
          tripType={tripType}
          isLoading={isAirportLoading}
        />
        <StackedItems
          className={classNames('flight-exchange-header__stacked-travelers', {
            'flight-exchange-header__stacked-travelers--hide-placeholder': !stackedTravelerslLabel,
          })}
          itemName={stackedTravelerslLabel}
          isPopoverVisible={isTravelerPopoverVisible}
          onClick={toggleTravelerPopover}
          onOutsideClick={closeTravelerPopover}
        >
          {travelers.map(({ id, firstName, lastName, avatarUrl }) => (
            <Avatar key={id} firstName={firstName} lastName={lastName} src={avatarUrl} />
          ))}
        </StackedItems>
        {items.map(({ id, label, text, href }) => (
          <ExchangeHeaderItem key={id} label={label} text={text} href={href} />
        ))}
      </div>
    </div>
  );
};

ExchangeHeader.propTypes = {
  exchange: PropTypes.shape({
    orderId: PropTypes.number.isRequired,
    orderItemId: PropTypes.number.isRequired,
    returnToPaths: PropTypes.shape({
      itemPath: PropTypes.string,
      overviewPath: PropTypes.string,
    }),
    supplierInfo: PropTypes.shape({
      channel: PropTypes.string,
      account: PropTypes.string,
      identifier: PropTypes.string,
    }),
    journeyElementId: PropTypes.number.isRequired,
  }).isRequired,
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatarUrl: PropTypes.string,
    }),
  ).isRequired,
  airports: PropTypes.objectOf(AirportShape).isRequired,
  trips: PropTypes.arrayOf(
    PropTypes.shape({
      origin: PropTypes.string,
      destination: PropTypes.string,
      date: PropTypes.string,
    }),
  ).isRequired,
  isAirportLoading: PropTypes.bool.isRequired,
  fetchJourneyElementApi: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  isAirportLoading: Object.values(state.flights.airportLoading).some(isLoading => isLoading),
});

const mapDispatchToProps = dispatch => ({
  fetchJourneyElementApi: (type, id) => dispatch(fetchJourneyElement(type, id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExchangeHeader);
